import { Link } from 'react-router-dom';
import { categories } from '@/mocks/articles';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string;
  category: string;
  author: string;
  publishedAt: string;
  readCount: number;
  tags: string[];
  variant?: 'default' | 'large' | 'compact';
}

export default function ArticleCard({
  slug, title, excerpt, coverImage, category, author, publishedAt, readCount, tags, variant = 'default',
}: ArticleCardProps) {
  const cat = categories.find((c) => c.slug === category);

  if (variant === 'compact') {
    return (
      <Link to={`/article/${slug}`} className="group flex gap-4 p-3 rounded-xl hover:bg-background-100 transition-colors">
        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img src={coverImage} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
            {title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{excerpt}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
            <span className="whitespace-nowrap">{publishedAt}</span>
            <span className="whitespace-nowrap">{readCount.toLocaleString()} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'large') {
    return (
      <Link to={`/article/${slug}`} className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all">
        <div className="relative aspect-[16/9] overflow-hidden">
          <img src={coverImage} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h2 className="text-xl md:text-2xl font-bold text-white line-clamp-2" style={{ fontFamily: 'var(--font-heading)' }}>
              {title}
            </h2>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/article/${slug}`} className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300/50 transition-all">
      <div className="aspect-[16/10] overflow-hidden">
        <img src={coverImage} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
      </div>
      <div className="p-4">
        {cat && (
          <span className="inline-block px-2 py-0.5 text-xs rounded-md bg-accent-100 text-accent-700 font-medium mb-2">
            {cat.name}
          </span>
        )}
        <h3 className="text-base font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors" style={{ fontFamily: 'var(--font-heading)' }}>
          {title}
        </h3>
        <p className="text-sm text-foreground-500 mt-2 line-clamp-2">{excerpt}</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
          <span className="text-xs text-foreground-400">{publishedAt}</span>
          <span className="text-xs text-foreground-400">{readCount.toLocaleString()} 阅读</span>
        </div>
      </div>
    </Link>
  );
}